import type { Post } from '@travelwin/cms/types';
import invariant from 'tiny-invariant';

import { BlogPost } from '@/components/BlogPost';
import { Layout } from '@/components/Layout';
import { useCurrencyRevalidator } from '@/hooks/useCurrencyRevalidator';
import { getVisitorOrigin } from '@/server-only/visitorOrigin';
import { getConfig } from '@/services/api';
import {
  fetchPostBySlug,
  getLatestBlogPosts,
} from '@/services/BlogPostService/BlogPostService';
import { getProductOfferings } from '@/services/product/ProductOfferingService';
import {
  getAllCountries,
  getFeaturedCountries,
} from '@/services/resources/ResourceService';
import { getMediaUrlForSize } from '@/utils/mediaUtils';
import { findMetaMatch, MetaStack } from '@/utils/metaUtils';
import { extractPlanIds, getCurrency } from '@/utils/ssrUtils';
import {
  getLayoutHowDoesItWork,
  getLayoutPlans,
  getLayoutTestimonials,
} from '~/helpers/pageLayoutData';
import { getLocale } from '~/middleware/i18next';

import { getSessionId } from '../../src/server-only/sessions';
import { Route } from './+types/blog-post';

export const loader = async ({
  params,
  request,
  context,
}: Route.LoaderArgs) => {
  invariant(params.slug);
  const sessionId = await getSessionId(request);
  const visitorOrigin = await getVisitorOrigin(request);
  const locale = getLocale(context);

  const [config, post] = await Promise.all([
    getConfig({ sessionId, visitorOrigin }),
    fetchPostBySlug({
      sessionId,
      slug: params.slug,
      locale,
    }),
  ]);

  if (!post) {
    throw new Response('Not Found', { status: 404 });
  }

  const currency = getCurrency({ request, config });
  const planIds = extractPlanIds(post.content);

  const limitOfRelatedPosts = 3;

  const relatedPosts: Post[] = [];

  post.relatedPosts?.forEach((relatedPost) => {
    if (typeof relatedPost !== 'number') {
      relatedPosts.push(relatedPost);
    }
  });

  if (relatedPosts.length < limitOfRelatedPosts) {
    const excludedIds = [post.id].concat(
      relatedPosts.map((relatedPost) => relatedPost.id),
    );

    const additionalPosts = await getLatestBlogPosts({
      sessionId,
      locale,
      limit: limitOfRelatedPosts - relatedPosts.length,
      excludeIds: excludedIds,
    });

    relatedPosts.push(...additionalPosts);
  }

  const [
    plans,
    blockPlans,
    featuredCountries,
    countries,
    testimonials,
    howDoesItWork,
  ] = await Promise.all([
    planIds.length > 0
      ? getProductOfferings({
          sessionId,
          ids: planIds,
          currency,
          locale,
          visitorOrigin,
        })
      : Promise.resolve([]),
    getLayoutPlans({
      sessionId,
      pageLayout: post.layout,
      currency,
      locale,
      visitorOrigin,
    }),
    getFeaturedCountries(
      sessionId,
      getCurrency({ request, config }),
      visitorOrigin,
    ),
    getAllCountries(sessionId, visitorOrigin),
    getLayoutTestimonials(sessionId, post.layout, locale),
    getLayoutHowDoesItWork(sessionId, post.layout, locale),
  ]);

  return {
    post,
    relatedPosts,
    plans,
    blockPlans,
    featuredCountries,
    countries,
    testimonials,
    howDoesItWork,
  };
};

export const meta: Route.MetaFunction = ({ matches, data }: Route.MetaArgs) => {
  if (!data) return [];

  const rootMatch = findMetaMatch('root', matches);
  const metaStack = new MetaStack(rootMatch.meta);
  const { meta } = data.post;

  if (!meta) return [];
  const { title, description, image, structuredData } = meta;
  const metaTitle = title || data.post.title;
  metaStack.title({
    title: metaTitle,
    brandName: rootMatch.data.config.BRAND_NAME,
  });

  const metaDescription = description || data.post.summary;
  metaStack.description(metaDescription);

  const metaImage = image || data.post.mainImage;
  const imageUrl = metaImage
    ? getMediaUrlForSize(metaImage, 'w1080')
    : undefined;
  if (imageUrl) {
    metaStack.push({
      property: 'og:image',
      content: imageUrl,
    });
  }

  if (structuredData) {
    structuredData.forEach(({ jsonld }) => {
      metaStack.push({
        'script:ld+json': jsonld,
      });
    });
  }

  return metaStack.get();
};

export default function Component({ loaderData }: Route.ComponentProps) {
  useCurrencyRevalidator();
  const {
    post,
    relatedPosts,
    plans,
    featuredCountries,
    countries,
    blockPlans,
    testimonials,
    howDoesItWork,
  } = loaderData;

  if (!post) {
    return null;
  }

  return (
    <Layout>
      <BlogPost
        post={post}
        relatedPosts={relatedPosts}
        plans={plans}
        featuredCountries={featuredCountries}
        countries={countries}
        blockPlans={blockPlans}
        testimonials={testimonials}
        howDoesItWork={howDoesItWork}
      />
    </Layout>
  );
}
